import { logger } from '@automata/utils';
import { useEffect, useRef } from 'react';
import { useNodesInitialized, useStore } from 'reactflow';

import layoutAlgorithms, { type LayoutAlgorithmOptions } from './algorithms';
import { ZEROED_NODE_POSITION } from '../../constants';
import { moveNodes } from '../../features/graph/graphSlice';
import { useAppDispatch } from '../../hooks';

export type LayoutOptions = {
  algorithm: keyof typeof layoutAlgorithms;
} & LayoutAlgorithmOptions;

function useAutoLayout(options: LayoutOptions) {
  const dispatch = useAppDispatch();
  const nodesInitialized = useNodesInitialized();
  const hasAppliedLayout = useRef(false);

  // Here we are storing a map of the nodes and edges in the flow.
  const elements = useStore((state) => ({
    nodeMap: state.nodeInternals,
    edgeMap: state.edges.reduce(
      (acc, edge) => acc.set(edge.id, edge),
      new Map()
    ),
  }));

  useEffect(() => {
    // Only run layout once
    if (hasAppliedLayout.current) {
      return;
    }

    // Only run the layout if there are nodes and they have been initialized with
    // their dimensions
    if (!nodesInitialized || elements.nodeMap.size === 0) {
      return;
    }

    const nodes = [...elements.nodeMap.values()];

    // Default SDK workflow ingestion places all nodes at 0,0. Apply layout when this is true
    const needsLayout = nodes.every(
      ({ position }) =>
        position.x === ZEROED_NODE_POSITION.x &&
        position.y === ZEROED_NODE_POSITION.y
    );

    if (!needsLayout) {
      // Nodes already have good positions, mark as laid out
      hasAppliedLayout.current = true;
      return;
    }

    // The callback passed to `useEffect` cannot be `async` itself, so instead we
    // create an async function here and call it immediately afterwards.
    const runLayout = async () => {
      try {
        const layoutAlgorithm = layoutAlgorithms[options.algorithm];
        const nodes = [...elements.nodeMap.values()];
        const edges = [...elements.edgeMap.values()];

        const { nodes: nextNodes } = await layoutAlgorithm(
          nodes,
          edges,
          options
        );

        // Convert the layouted nodes to the format expected by the moveNodes action
        const nodeUpdates = Object.fromEntries(
          nextNodes.map((node) => [node.id, { position: node.position }])
        );

        // Update node positions in Redux store
        dispatch(moveNodes(nodeUpdates));

        // Mark that we have applied layout
        hasAppliedLayout.current = true;
      } catch (error) {
        logger.error('Auto-layout failed:', error);
        // Mark as applied to prevent infinite retries
        hasAppliedLayout.current = true;
      }
    };

    runLayout();
  }, [nodesInitialized, elements, options, dispatch]);
}

export default useAutoLayout;
